import _ from 'lodash';

const CLIENT_CHARGED_SERVICE_TYPE = 1;
const NON_CHARGED_SERVICE_TYPE = 2;
const ORDER_CANCELED_INTERN = 21;
const ORDER_CANCELED_EXTERN = 121;

const QuotationRules = {
  /**
   * Retrieves client charged other charges subtotal
   * @param {array} quotationItems - The list of quotation items.
   * @returns {number} Subtotal amount of charges that client must pay
   */
  getClientChargedOtherChargesSubtotal(quotationItems) {
    const totalAmountsPerQuotation = this.generateTotalAmountsPerQuotation(quotationItems);
    const clientChargedQuotations = totalAmountsPerQuotation.filter(
      (amountsItem) => amountsItem.isClientCharged,
    );
    return clientChargedQuotations.reduce(
      (total, quotation) => total + Number(quotation.otherChargesSubtotal),
      0,
    );
  },

  /**
   * Retrieves a list of total amounts items per quotation
   * @param {array} quotationItems - The list of quotation items.
   * @returns {array} A list of total amounts per quotation.
   */
  generateTotalAmountsPerQuotation(quotationItems) {
    const quotationItemsByQuotationId = _.uniqBy(quotationItems, 'quotation_id');

    return quotationItemsByQuotationId.map((quotation) => ({
      quotationId: quotation.quotation_id,
      activityId: quotation.order_activity_id,
      isClientCharged: quotation.activity_service_type === 1,
      subtotal: quotation.subtotal,
      otherChargesSubtotal: quotation.otherChargesAmount,
      taxes: quotation.taxesAmount,
      total: quotation.amount,
    }));
  },

  /**
   * Determines if a given quotation item is a sparepart
   * @param {object} quotationItem - The quotation item.
   * @returns {boolean} True if the quotation item is a sparepart, false otherwise
   */
  isSparepart(quotationItem) {
    const sparepartCategories = ['PA', 'SI', 'CP'];

    return !quotationItem.pack_id_dms
      && sparepartCategories.includes(quotationItem.pack_category)
      && quotationItem.part_brand !== 'manoDeObra';
  },

  /**
   * Determines if a given quotation item is a labour
   * @param {object} quotationItem - The quotation item.
   * @returns {boolean} True if the quotation item is a labour, false otherwise
   */
  isLabour(quotationItem) {
    return !quotationItem.pack_id_dms && quotationItem.part_brand === 'manoDeObra';
  },

  /**
   * Determines if a given quotation item is an otherCharge
   * @param {object} quotationItem - The quotation item.
   * @returns {boolean} True if the quotation item is an other charge, false otherwise
   */
  isOtherCharge(quotationItem) {
    return quotationItem.material_cost && quotationItem.pack_category !== 'PP';
  },

  /**
   * Determines if a given quotation item is a PPSo item
   * @param {object} quotationItem - The quotation item.
   * @returns {boolean} True if the quotation item is a PPSo item, false otherwise
   */
  isPpsoItem(quotationItem) {
    return quotationItem.pack_category === 'PP';
  },

  /**
   * Determines if a given quotation item is an agency package item
   * @param {object} quotationItem - The quotation item.
   * @returns {boolean} True if the quotation item is an agency package item, false otherwise
   */
  isAgencyPackageItem(quotationItem) {
    return !!(quotationItem.pack_id_dms && quotationItem.pack_category === 'DP');
  },

  /**
   * Generates a hash map of quotation items grouped by their related activity.
   * @param {array} quotationItems - The list of quotation items.
   * @returns {object} A hash map of quotation items grouped by their related activity.
   */
  groupQuotationItemsByActivity(quotationItems) {
    return _.groupBy(quotationItems, (item) => item.order_activity_id);
  },

  /**
   * Retrieves the subtotal amount of a given activity.
   * @param {number} activityId - The activity unique identifier.
   * @param {array} quotationItems - The list of quotation items.
   * @returns {number} The subtotal amount of the given activity.
   */
  getSubtotalAmountOfActivity(activityId, quotationItems) {
    const groupedItemsByActivity = QuotationRules.groupQuotationItemsByActivity(quotationItems);
    const filteredGroupedItemsByQuotation = _.uniqBy(groupedItemsByActivity[activityId], 'quotation_id');
    return filteredGroupedItemsByQuotation.reduce(
      (subtotalAmount, quotationItem) => subtotalAmount
        + Number(quotationItem.subtotal),
      0,
    ).toFixed(2);
  },

  /**
   * Retrieves a list of non charged quotation items.
   * @param {array} quotationItems - The list of quotation items.
   * @returns {array} A list of non charged quotation items.
   */
  getNonChargedQuotationItems(quotationItems) {
    return quotationItems.filter(
      (quotationItem) => quotationItem.activity_service_type === NON_CHARGED_SERVICE_TYPE,
    );
  },

  /**
   * Retrieves a list of client charged quotation items.
   * @param {array} quotationItems - The list of quotation items.
   * @returns {array} A list of client charged quotation items.
   */
  getClientChargedQuotationItems(quotationItems) {
    return quotationItems.filter(
      (quotationItem) => quotationItem.activity_service_type === CLIENT_CHARGED_SERVICE_TYPE,
    );
  },

  /**
   * Retrieves a list of rejected quotation items.
   * @param {array} quotationItems - The list of quotation items.
   * @returns {array} A list of rejected quotation items.
   */
  getRejectedQuotationItems(quotationItems) {
    return quotationItems.filter((quotationItem) => !quotationItem.accepted);
  },

  /**
   * Determines if a given quotation item is a PPSo charge.
   * @param {object} quotationItem - The quotation item.
   * @returns {boolean} True if the quotation item is a PPSo charge, false otherwise
   */
  isPpsoCharge(quotationItem) {
    return !!(quotationItem.material_desc && quotationItem.material_id_dms) && quotationItem.pack_category === 'PP';
  },

  /**
   * Determines if a given ppso charge item is included.
   * @param {object} ppsoChargeItem - The ppso charge item.
   * @returns {boolean} True if the ppso charge item is included, false otherwise.
   */
  isPpsoChargeIncluded(ppsoChargeItem) {
    return ppsoChargeItem.isIncluded === 1;
  },

  /**
   * Determines if there is any of the quotation items marked as accepted.
   * @param {*} quotationItems - The list of quotation items.
   * @returns True if there is any of the quotation items marked as accepted, false otherwise.
   */
  isAnyActivityAccepted(quotationItems) {
    if (!quotationItems) {
      return false;
    }

    return quotationItems.some(
      (item) => item.activity_service_type === CLIENT_CHARGED_SERVICE_TYPE && item.accepted,
    );
  },

  /**
   * Maps accepted values received from backend into UI state.
   * @param {*} accepted - Raw accepted value from backend.
   * @returns {boolean|null} Accepted state as boolean or null.
   */
  mapAcceptedState(accepted) {
    if (accepted === 1 || accepted === '1' || accepted === true) {
      return true;
    }

    if (accepted === 0 || accepted === '0' || accepted === false) {
      return false;
    }

    return null;
  },

  /**
   * Validates if two identifiers have the same value.
   * @param {*} left - Left identifier value.
   * @param {*} right - Right identifier value.
   * @returns {boolean} True when both identifiers are set and equal.
   */
  isSameIdentifier(left, right) {
    return left != null && right != null && String(left) === String(right);
  },

  /**
   * Updates 'accepted' and 'view_status' values of a frozen quotation items list.
   * @param {Array} frozenQuotationItems - The list of frozen quotation items.
   * @param {Array} originalQuotationItems - The list of original quotation items.
   * @returns {Array} The updated frozen quotation items list.
   */
  assignAcceptedValuesToFrozenQuotation(frozenQuotationItems, originalQuotationItems) {
    return frozenQuotationItems.map((quotationItem) => {
      const originalQuotationItem = this.findOriginalQuotationItem(
        quotationItem,
        originalQuotationItems,
      );
      const hasOriginalItem = !!originalQuotationItem;

      if (quotationItem.activity_service_type === CLIENT_CHARGED_SERVICE_TYPE) {
        const accepted = hasOriginalItem
          ? this.mapAcceptedState(originalQuotationItem.accepted)
          : this.mapAcceptedState(quotationItem.accepted);
        const viewStatus = hasOriginalItem
          ? originalQuotationItem.view_status
          : quotationItem.view_status;

        return {
          ...quotationItem,
          accepted,
          view_status: viewStatus,
        };
      }

      return {
        ...quotationItem,
        accepted: true,
        view_status: hasOriginalItem
          ? originalQuotationItem.view_status
          : quotationItem.view_status,
      };
    });
  },

  /**
   * Retrieves the original quotation item of a given frozen quotation item.
   * @param {Object} frozenQuotationItem - The frozen quotation item.
   * @param {Object} originalQuotationItems - The list of original quotation items.
   * @returns {Object} The original quotation item of the given frozen quotation item.
   */
  findOriginalQuotationItem(frozenQuotationItem, originalQuotationItems) {
    const items = Array.isArray(originalQuotationItems) ? originalQuotationItems : [];

    if (this.isPpsoCharge(frozenQuotationItem)) {
      const ppsoCharge = items.find(
        (item) => this.isSameIdentifier(item.pack_id, frozenQuotationItem.pack_id),
      );

      if (ppsoCharge) {
        return ppsoCharge;
      }
    }

    return items.find(
      (item) => {
        const sameActivity = this.isSameIdentifier(
          item.order_activity_id,
          frozenQuotationItem.order_activity_id,
        );

        const byPartId = this.isSameIdentifier(item.part_id, frozenQuotationItem.part_id);
        const byMaterialId = this.isSameIdentifier(
          item.material_id_dms,
          frozenQuotationItem.material_id_dms,
        );
        const byPackId = this.isSameIdentifier(item.pack_id, frozenQuotationItem.pack_id);
        const byQuotationId = this.isSameIdentifier(
          item.quotation_id,
          frozenQuotationItem.quotation_id,
        );

        return (sameActivity && (byPartId || byMaterialId || byPackId || byQuotationId))
          || byPartId;
      },
    );
  },

  /**
   * Retrieves a list of activities that have been accepted.
   * @param {Array} quotationItems - The list of quotation items.
   * @returns {Array} A list of activities that have been accepted.
   */
  getAcceptedActivities(quotationItems) {
    const uniqItemByActivityList = _.uniqBy(quotationItems, 'order_activity_id');
    const clientChargedQuotationItems = this.getClientChargedQuotationItems(uniqItemByActivityList);
    const acceptedQuotationItems = clientChargedQuotationItems.filter((item) => item.accepted);
    return acceptedQuotationItems.map((item) => item.order_activity_id);
  },
  /**
   * Obtenemos solo los presupuestos de las ordenes abiertas
   * @param {Array} quotationItems - The list of frozen quotation items.
   * @returns {Array} A list of activities that are not in canceled orders
   */
  getQuotationItemOrdersNoCanceled(quotationItems) {
    const orders = JSON.parse(localStorage.getItem('ordersLogin'));
    const quotationFrozen = [];
    orders.forEach((order) => {
      if (order.orderStatus !== ORDER_CANCELED_EXTERN
          && order.orderStatus !== ORDER_CANCELED_INTERN) {
        quotationItems.forEach((quotation) => {
          if (quotation.orderId === order.orderId) {
            quotationFrozen.push(quotation);
          }
        });
      }
    });

    return quotationFrozen;
  },
  /**
   * Obtenemos la orden actual de la superorden
   * @returns {Array} order.
   */
  getOrderActual() {
    const orders = JSON.parse(localStorage.getItem('ordersLogin'));
    return orders[orders.length - 1];
  },
  /**
   * Obtenemos la orden principal de la superorden
   * @returns {Array} order.
   */
  getOrderPrincipal() {
    const orders = JSON.parse(localStorage.getItem('ordersLogin'));
    return orders[0];
  },
  /**
   * Agrupamos los items de ppso por su pack_id
   * @param {array} packagesPpso
   * @returns {array} ppsoByGroup
   */
  getPPsOByPackages(packagesPpso) {
    const ppsoByGroup = [];
    const map = new Map();
    packagesPpso.forEach((item) => {
      if (!map.has(item.pack_id)) {
        map.set(item.pack_id, true);
        ppsoByGroup.push({
          orderId: item.order_id,
          orderIdDMS: item.orderIdDMS,
          pack_desc: item.pack_desc,
          pack_price: item.pack_price,
          pack_id: item.pack_id,
        });
      }
    });
    return ppsoByGroup;
  },

};

export default QuotationRules;
